import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { jod } from "@astroplanet/shared";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { type LiveShift, manageKey, useLiveShifts, useShifts } from "../../hooks/manage";
import { presetRange, ymd } from "../../hooks/reports";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { money } from "../../lib/format";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { DownloadButton } from "../ui/DownloadButton";
import { Modal } from "../ui/Modal";
import { PrintButton } from "../ui/PrintButton";

const PRESETS = ["today", "yesterday", "week", "month"] as const;
const dur = (m: number) => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;

function ForceClose({ s, onClose }: { s: LiveShift; onClose: () => void }) {
  const { t } = useTranslation();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const [counted, setCounted] = useState("");
  const [why, setWhy] = useState("");
  const [busy, setBusy] = useState(false);
  const fils = counted.trim() === "" ? NaN : jod(Number(counted));
  const variance = Number.isNaN(fils) ? null : fils - s.report.expectedCash;
  async function close() {
    setBusy(true);
    try {
      await api.post(`/shifts/${s.id}/close`, { countedCash: fils, notes: why.trim() });
      await qc.invalidateQueries({ queryKey: manageKey });
      toast(t("shiftControl.closedOk"), "success");
      onClose();
    } catch (e) { toastError(e); } finally { setBusy(false); }
  }
  return (
    <Modal open size="sm" onClose={onClose} title={t("shiftControl.forceTitle", { name: s.cashier })}
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-danger" disabled={busy || Number.isNaN(fils) || why.trim().length < 3} onClick={() => void close()}>🔒 {t("shiftControl.closeNow")}</button></>}>
      <p className="text-xs font-semibold text-gray-600">{t("shiftControl.forceHelp")}</p>
      <div className="rounded-xl bg-gray-50 p-3 text-xs font-bold">{t("shiftControl.expected")}: <span className="num font-mono text-cosmic-purple">{money(s.report.expectedCash)} {t("currency")}</span></div>
      <div><label className="label" htmlFor="fc-counted">{t("shiftControl.counted", { cur: t("currency") })}</label><input id="fc-counted" className="field num font-mono" inputMode="decimal" value={counted} onChange={(e) => setCounted(e.target.value)} /></div>
      {variance !== null && <Pill tone={variance === 0 ? "green" : "red"}>{t("shiftControl.variance")}: {variance > 0 ? "+" : ""}{money(variance)}</Pill>}
      <div><label className="label" htmlFor="fc-why">{t("shiftControl.reason")}</label><input id="fc-why" className="field" maxLength={300} value={why} onChange={(e) => setWhy(e.target.value)} /></div>
    </Modal>
  );
}

function Live({ scope }: { scope: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const q = useLiveShifts(scope);
  const [closing, setClosing] = useState<LiveShift | null>(null);
  const list = q.data?.shifts ?? [];
  if (q.isLoading) return <Empty>{t("common.loading")}</Empty>;
  if (!list.length) return <Empty>{t("shiftControl.noneOpen")}</Empty>;
  const drawers = list.reduce((a, s) => a + s.report.expectedCash, 0);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2 text-xs font-bold sm:grid-cols-3" data-testid="live-totals">
        <div className="card p-2.5">{t("shiftControl.openCount")}: <span className="num font-mono">{list.length}</span></div>
        <div className="card p-2.5">{t("shiftControl.cashInDrawers")}: <span className="num font-mono text-cosmic-purple">{money(drawers)} {t("currency")}</span></div>
        <div className="card p-2.5">{t("shiftControl.takingsNow")}: <span className="num font-mono">{money(list.reduce((a, s) => a + s.report.grossSales, 0))} {t("currency")}</span></div>
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        {list.map((s) => (
          <div key={s.id} data-live={s.cashier} className="card space-y-2 p-3.5">
            <div className="flex items-start justify-between gap-2">
              <div><div className="text-sm font-black text-cosmic-navy">{s.cashier}</div><div className="text-[11px] text-gray-500">{s.terminal ?? ""} · {s.branch} · {t("shiftControl.openFor", { time: dur(s.minutesOpen) })}</div></div>
              <Pill tone="green" pulse>{t("shiftControl.live")}</Pill>
            </div>
            <div className="grid grid-cols-2 gap-1 text-xs font-bold text-gray-700">
              <div>{t("shiftControl.expected")}: <span className="num font-mono text-cosmic-purple">{money(s.report.expectedCash)}</span></div>
              <div>{t("shiftControl.takings")}: <span className="num font-mono">{money(s.report.grossSales)}</span></div>
              <div>{t("shiftControl.receipts")}: <span className="num font-mono">{s.report.transactionCount}</span>{s.report.voidCount > 0 && <span className="ms-1 text-red-600">({t("shiftControl.voids", { n: s.report.voidCount })})</span>}</div>
              <div>{t("shiftControl.lastSale")}: <span className="num font-mono">{s.report.lastSaleAt ? new Date(s.report.lastSaleAt).toLocaleTimeString(locale === "ar" ? "ar-JO" : "en-GB", { hour: "2-digit", minute: "2-digit" }) : "—"}</span></div>
              <div>{t("shiftControl.cashIn")}: <span className="num font-mono">{money(s.report.cashIn)}</span></div>
              <div>{t("shiftControl.cashOut")}: <span className="num font-mono">{money(s.report.cashOut)}</span></div>
              <div>{t("shiftControl.card")}: <span className="num font-mono">{money(s.report.cardTotal)}</span></div>
              <div>{t("shiftControl.refunds")}: <span className="num font-mono">{money(s.report.refundTotal)}</span></div>
            </div>
            {s.report.movements.length > 0 && <ul className="rounded-xl bg-gray-50 p-2 text-[11px] font-semibold text-gray-600">{s.report.movements.map((m) => <li key={m.id}>{m.kind === "in" ? "➕" : "➖"} <span className="num font-mono">{money(m.amount)}</span> · {m.reason} · {m.by}</li>)}</ul>}
            <div className="flex flex-wrap gap-2">
              <PrintButton path={`/documents/z-report/${s.id}?locale=${locale}`} className="btn-ghost !px-2.5 !py-1 text-xs">{t("shiftControl.xReport")}</PrintButton>
              <DownloadButton path={`/documents/z-report/${s.id}?locale=${locale}`} fallbackName="X-Report.pdf" className="btn-ghost !px-2.5 !py-1 text-xs">📄 PDF</DownloadButton>
              <button type="button" className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setClosing(s)}>🔒 {t("shiftControl.forceClose")}</button>
            </div>
          </div>
        ))}
      </div>
      {closing && <ForceClose s={closing} onClose={() => setClosing(null)} />}
    </div>
  );
}

function History({ from, to, scope }: { from: string; to: string; scope: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const q = useShifts(from, to, scope);
  const list = q.data?.shifts ?? [];
  if (q.isLoading) return <Empty>{t("common.loading")}</Empty>;
  if (!list.length) return <Empty>{t("shiftControl.noShifts")}</Empty>;
  return (
    <div className="space-y-2">
      <p className="text-xs font-black text-gray-600">{t("shiftControl.netVariance")}: <span className={`num font-mono ${q.data!.variance === 0 ? "text-emerald-700" : "text-red-600"}`}>{money(q.data!.variance)} {t("currency")}</span></p>
      <div className="overflow-x-auto">
        <table className="w-full text-start text-xs">
          <thead className="border-b border-gray-100 bg-gray-50 text-[10px] font-black uppercase tracking-wider text-gray-500">
            <tr><th className="px-3 py-2 text-start">{t("shiftControl.cashier")}</th><th className="px-2 py-2 text-start">{t("shiftControl.opened")}</th><th className="px-2 py-2 text-start">{t("shiftControl.closed")}</th><th className="px-2 py-2 text-end">{t("shiftControl.receipts")}</th><th className="px-2 py-2 text-end">{t("shiftControl.takings")}</th><th className="px-2 py-2 text-end">{t("zreport.counted")}</th><th className="px-2 py-2 text-end">{t("shiftControl.variance")}</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody className="divide-y divide-gray-100 font-bold text-gray-700">
            {list.map((s) => (
              <tr key={s.id} data-shift={s.cashier}>
                <td className="px-3 py-2">{s.cashier}<div className="text-[10px] text-gray-400">{s.terminal ?? ""} {s.branch}</div></td>
                <td className="num px-2 py-2 font-mono">{s.opened}</td>
                <td className="num px-2 py-2 font-mono">{s.closed ?? <Pill tone="green">{t("shiftControl.live")}</Pill>}{s.closed_by_name && !s.self_closed && <div className="text-[10px] font-semibold text-amber-700">{t("shiftControl.closedBy", { name: s.closed_by_name })}</div>}</td>
                <td className="num px-2 py-2 text-end">{s.receipts}</td><td className="num px-2 py-2 text-end font-mono">{money(s.takings)}</td>
                <td className="num px-2 py-2 text-end font-mono">{s.counted_cash === null ? "—" : money(s.counted_cash)}</td>
                <td className={`num px-2 py-2 text-end font-mono ${s.variance ? "text-red-600" : "text-emerald-700"}`}>{s.variance === null ? "—" : money(s.variance)}</td>
                <td className="px-3 py-2 text-end"><DownloadButton path={`/documents/z-report/${s.id}?locale=${locale}`} fallbackName="Z-Report.pdf" className="btn-ghost !px-2 !py-0.5 text-[11px]">📄 Z</DownloadButton><PrintButton path={`/documents/z-report/${s.id}?locale=${locale}`} className="btn-ghost !px-2 !py-0.5 text-[11px]">{""}</PrintButton></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Shift control: who has a drawer open right now (live cash, takings, last sale), close any shift with a reason, and the history with Z-reports. */
export function ShiftControlPanel() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState<"live" | "history">("live");
  const [range, setRange] = useState(presetRange("today"));
  const [preset, setPreset] = useState<string>("today");
  const [scope, setScope] = useState("branch");
  return (
    <div className="space-y-5">
      <div className="card flex items-center gap-3.5 p-5">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-cosmic-purple to-solar-orange text-2xl shadow-cosmic" aria-hidden>💰</div>
        <div><h1 className="text-xl font-black tracking-tight">{t("shiftControl.title")}</h1><p className="text-xs font-medium text-gray-500">{t("shiftControl.subtitle")}</p></div>
      </div>
      <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label={t("shiftControl.title")}>
        {(["live", "history"] as const).map((x) => <button key={x} role="tab" type="button" aria-selected={tab === x} onClick={() => setTab(x)} className={`rounded-xl px-4 py-2 text-xs font-black ${tab === x ? "bg-cosmic-purple text-white shadow-cosmic" : "bg-white text-gray-600 hover:bg-purple-50"}`}>{t(`shiftControl.tabs.${x}`)}</button>)}
        {isAdmin && <select className="field !w-auto !py-1.5" aria-label={t("reports.scope")} value={scope} onChange={(e) => setScope(e.target.value)}><option value="branch">{t("reports.thisBranch")}</option><option value="all">{t("reports.allBranches")}</option></select>}
      </div>
      <section className="card space-y-3 p-4 sm:p-5" aria-label={t(`shiftControl.tabs.${tab}`)}>
        <SectionHeader title={t(`shiftControl.tabs.${tab}`)} />
        {tab === "history" && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-wrap gap-1 text-[11px] font-black" role="group" aria-label={t("reports.period")}>
              {PRESETS.map((p) => <button key={p} type="button" aria-pressed={preset === p} onClick={() => { setPreset(p); setRange(presetRange(p)); }} className={`rounded-lg px-2.5 py-1 ${preset === p ? "bg-cosmic-navy text-white" : "bg-gray-100 text-gray-700"}`}>{t(`reports.presets.${p}`)}</button>)}
            </div>
            <div><label className="label" htmlFor="sh-from">{t("reports.from")}</label><input id="sh-from" className="field num !py-1.5" type="date" value={range.from} max={ymd(new Date())} onChange={(e) => { setPreset("custom"); setRange({ ...range, from: e.target.value }); }} /></div>
            <div><label className="label" htmlFor="sh-to">{t("reports.to")}</label><input id="sh-to" className="field num !py-1.5" type="date" value={range.to} max={ymd(new Date())} onChange={(e) => { setPreset("custom"); setRange({ ...range, to: e.target.value }); }} /></div>
          </div>
        )}
        {tab === "live" ? <Live scope={scope} /> : <History from={range.from} to={range.to} scope={scope} />}
      </section>
    </div>
  );
}
